import React from 'react';
import type { PersonalInfo } from '@/types';
import { Card } from '@/components/ui/card';
import { ValidationBadge } from '@/components/search/ValidationBadge';
import { formatPhoneNumber } from '@/utils/searchValidation';
import { cn } from '@/lib/utils';
import {
  MapPin,
  Phone,
  Mail,
  Users,
  Home,
  Calendar,
  CheckCircle,
  Briefcase,
} from 'lucide-react';

interface EnrichedDataDisplayProps {
  personalInfo: PersonalInfo;
  className?: string;
}

export const EnrichedDataDisplay: React.FC<EnrichedDataDisplayProps> = ({ personalInfo, className }) => {
  // Display ALL data - no truncation for any enrichment data
  const displayedAddresses = personalInfo.addresses;
  const displayedRelatives = personalInfo.relatives;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Header Card */}
      <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">{personalInfo.fullName}</h2>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              {personalInfo.dob && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>DOB: {personalInfo.dob}</span>
                </div>
              )}
              {personalInfo.age && <div>Age: {personalInfo.age} Years Old</div>}
            </div>
            <div className="mt-2 space-y-1 text-xs text-slate-600">
              <div>Identity Confidence: {personalInfo.identityConfidenceScore}%</div>
              <div>Fraud Risk: {personalInfo.fraudRisk}/100</div>
              <div>First Seen: {new Date(personalInfo.firstSeen).toLocaleDateString()}</div>
              <div>Last Seen: {new Date(personalInfo.lastSeen).toLocaleDateString()}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Addresses Section */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-slate-100 rounded-lg">
            <MapPin className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Address History</h3>
            <p className="text-xs text-slate-500">{personalInfo.addresses.length} addresses found</p>
          </div>
        </div>

        <div className="space-y-3">
          {displayedAddresses.map((address, idx) => (
            <div
              key={idx}
              className={cn(
                'p-4 rounded-lg border-2 transition-all',
                address.primary
                  ? 'border-green-200 bg-green-50'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300'
              )}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Home className={cn('w-4 h-4', address.primary ? 'text-green-600' : 'text-slate-500')} />
                    <span className="font-medium text-slate-900">
                      {address.street} {address.unit && `Unit ${address.unit}`}
                    </span>
                    <ValidationBadge status={{ validated: address.validated, primary: address.primary }} />
                  </div>
                  <p className="text-sm text-slate-600">
                    {address.city}, {address.state} {address.zip}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Phones */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Phone className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Phone Numbers</h3>
              <p className="text-xs text-slate-500">{personalInfo.phones.length} numbers found</p>
            </div>
          </div>

          <div className="space-y-3">
            {personalInfo.phones.map((phone, idx) => (
              <div
                key={idx}
                className={cn(
                  'p-3 rounded-lg border',
                  phone.primary ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'
                )}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-slate-900">{formatPhoneNumber(phone.number)}</span>
                  <div className="flex items-center gap-2">
                    <ValidationBadge status={{ validated: phone.validated, primary: phone.primary }} />
                    {phone.isDNC && (
                      <span className="px-2 py-0.5 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                        DNC
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate-600 space-y-0.5">
                  <p>{phone.type}</p>
                  {phone.carrier && <p className="text-slate-500">{phone.carrier}</p>}
                  <p className="text-slate-500">Last Reported: {phone.lastReportedDate}</p>
                  <p className={phone.isConnected ? 'text-green-600' : 'text-red-600'}>
                    {phone.isConnected ? 'Connected' : 'Disconnected'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Emails */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Mail className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Email Addresses</h3>
              <p className="text-xs text-slate-500">{personalInfo.emails.length} emails found</p>
            </div>
          </div>

          <div className="space-y-2">
            {personalInfo.emails.map((email, idx) => (
              <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-900 break-all">{email.address}</span>
                  <div className="flex items-center gap-2">
                    <ValidationBadge status={{ validated: email.isValidated, primary: email.primary }} />
                    {email.isBusiness && (
                      <span className="px-2 py-0.5 text-xs font-semibold text-slate-700 bg-slate-100 rounded-full">
                        Business
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Employers */}
      {personalInfo.employers.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Briefcase className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Employers</h3>
              <p className="text-xs text-slate-500">{personalInfo.employers.length} employers found</p>
            </div>
          </div>

          <div className="space-y-3">
            {personalInfo.employers.map((employer, idx) => (
              <div key={idx} className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="font-medium text-slate-900">{employer.name}</div>
                <div className="text-sm text-slate-600 mt-1">{employer.address}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Relatives */}
      {personalInfo.relatives.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Relatives</h3>
              <p className="text-xs text-slate-500">{personalInfo.relatives.length} relatives found</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {displayedRelatives.map((relative, idx) => (
              <div key={idx} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div className="font-medium text-slate-900">{relative.name}</div>
                <div className="text-xs text-slate-600 space-y-0.5 mt-1">
                  {relative.relationshipType && <p>{relative.relationshipType}</p>}
                  {relative.dateOfBirth && <p>DOB: {relative.dateOfBirth}</p>}
                  {relative.age && <p>Age: {relative.age}</p>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

